import FJImageStrokeManagerSingleton from '@fj/fj-image-stroke';
import FJPoolStore from '../../../../store/FJPoolStore';
import FJCanvasUtil from '../../../../util/FJCanvasUtil';
import FJUtil from '../../../../util/FJUtil';
import FJMaskCanvas from './FJMaskCanvas';
import FJOriginCanvas from './FJOriginCanvas';

export const BACKGROUND_TYPE = {
    IMAGE: 'image',
    COLOR: 'color',
    TRANSPARENT: 'transparent',
};

// 图片去背景画布
class FJPhotoRemoveBgCanvas extends FJOriginCanvas {
    _canvas = null;
    _ctx = null;

    _maskCanvas = null; // FJMaskCanvas

    _backgroundCanvas = null;
    _backgroundCanvasCtx = null;

    _backgroundImage = null;
    _backgroundColor = null;
    _backgroundImageLoaded = false;

    _backgroundImageRect = null;
    _backgroundType = BACKGROUND_TYPE.TRANSPARENT;
    _backgroundImageInfo = null;

    _strokeInfo = null;
    _transparentSize = 14;

    constructor() {
        super();
        this._canvas = FJPoolStore.getCanvas('2d', true);
        this._ctx = this._canvas.getContext('2d');
    }

    async init(editImageUrl) {
        await super.init(editImageUrl);

        this.updateView();
        this.updateBackgroundCanvas();
    }

    _fitBackgroundImage() {
        if (!this._backgroundImage) return;

        const size = this.getSize();
        const bgSize = {
            width: this._backgroundImage.width,
            height: this._backgroundImage.height,
        };

        const canvasAspect = size.width / size.height;
        const imageAspect = bgSize.width / bgSize.height;

        if (canvasAspect < imageAspect) {
            bgSize.height = size.height;
            bgSize.width = size.height * imageAspect;
        } else {
            bgSize.width = size.width;
            bgSize.height = size.width / imageAspect;
        }

        this._backgroundImageRect = {
            x: (size.width - bgSize.width) / 2,
            y: (size.height - bgSize.height) / 2,
            width: bgSize.width,
            height: bgSize.height,
        };
    }

    updateBackgroundCanvas() {
        const size = this.getSize();
        if (!size) return;

        if (!this._backgroundCanvas) {
            this._backgroundCanvas = FJPoolStore.getCanvas('2d', true);
            this._backgroundCanvasCtx = this._backgroundCanvas.getContext('2d');
        }

        this._backgroundCanvas.width = size.width;
        this._backgroundCanvas.height = size.height;

        switch (this._backgroundType) {
            case BACKGROUND_TYPE.TRANSPARENT: {
                const transparentSize = Math.round(
                    ((this._transparentSize / 1080) * size.width) / (size.width / size.height),
                );

                FJCanvasUtil.drawTransparentBackground(
                    this._backgroundCanvasCtx,
                    size.width,
                    size.height,
                    transparentSize,
                );
                break;
            }
            case BACKGROUND_TYPE.COLOR:
                this._backgroundCanvasCtx.save();
                this._backgroundCanvasCtx.fillStyle = this._backgroundColor;
                this._backgroundCanvasCtx.fillRect(0, 0, size.width, size.height);
                this._backgroundCanvasCtx.restore();
                break;
            case BACKGROUND_TYPE.IMAGE: {
                if (!this._backgroundImage || !this._backgroundImageRect) return;

                this._backgroundCanvasCtx.drawImage(
                    this._backgroundImage,
                    this._backgroundImageRect.x,
                    this._backgroundImageRect.y,
                    this._backgroundImageRect.width,
                    this._backgroundImageRect.height,
                );
                break;
            }
        }
    }

    _applyStroke() {
        if (!this._strokeInfo || this._strokeInfo.type === 'none') return;

        const size = this.getSize();
        FJImageStrokeManagerSingleton.applyImageStroke(this._canvas, this._strokeInfo, {
            originalWidth: size.width,
            originalHeight: size.height,
        });
        FJImageStrokeManagerSingleton.applyImageStrokeByShadow(this._canvas, this._strokeInfo);
    }

    updateView() {
        const size = this.getSize();
        if (!size) return;
        this._canvas.width = size.width;
        this._canvas.height = size.height;
        this._ctx.clearRect(0, 0, size.width, size.height);
        const rect = { x: 0, y: 0, width: size.width, height: size.height };

        this._maskCanvas?.draw(this._ctx, rect);

        this._ctx.save();
        this._ctx.globalCompositeOperation = 'source-in';
        super.draw(this._ctx, rect);
        this._ctx.restore();

        this._applyStroke();
    }

    getMaskData() {
        if (!this._maskCanvas) return null;
        return this._maskCanvas.getMaskData();
    }

    setMaskData(maskData) {
        if (!this._maskCanvas) {
            this._maskCanvas = new FJMaskCanvas();
        }
        this._maskCanvas.init(this.getSize());
        if (maskData) {
            this._maskCanvas.setMaskData(maskData);
        } else {
            this._maskCanvas.clear();
        }
    }

    draw(context, rect, options) {
        if (!this._editLoaded) return;
        const { isShowPhotoRemoveBg, isCompare, isNotDrawTransparentBG = false } = options;

        if (!isShowPhotoRemoveBg || isCompare) {
            super.draw(context, rect, options);
            return;
        }

        context.drawImage(this._canvas, rect.x, rect.y, rect.width, rect.height);
        if (
            (isNotDrawTransparentBG && this._backgroundType === BACKGROUND_TYPE.TRANSPARENT) ||
            !this._backgroundCanvas ||
            this._backgroundCanvas.width === 0 ||
            this._backgroundCanvas.height === 0
        )
            return;

        context.save();
        context.globalCompositeOperation = 'destination-over';
        context.drawImage(this._backgroundCanvas, rect.x, rect.y, rect.width, rect.height);
        context.restore();
    }

    async setBackgroundImage(setBackgroundImage) {
        this._backgroundImageInfo = setBackgroundImage;
        let backgroundImageUrl = setBackgroundImage.url;

        const isCrossOrigin = FJUtil.isCrossOrigin(backgroundImageUrl);

        if (isCrossOrigin) {
            // 下载图片
            backgroundImageUrl = await FJUtil.getBlobByUrlSolveCrossOrigin(backgroundImageUrl);
        }
        await new Promise((resolve, reject) => {
            // 判断editImageUrl 是否跨域
            this._backgroundImage = new Image();
            this._backgroundImage.src = backgroundImageUrl;

            this._backgroundImage.onload = () => {
                this._backgroundImageLoaded = true;
                resolve();
            };

            this._backgroundImage.onerror = () => {
                reject();
            };
        });

        this._backgroundType = BACKGROUND_TYPE.IMAGE;
        this._fitBackgroundImage();
        this.updateBackgroundCanvas();
    }

    async setBackgroundInfo(info) {
        const { backgroundType, backgroundImage, backgroundColor } = info;
        if (backgroundType === BACKGROUND_TYPE.IMAGE) {
            await this.setBackgroundImage(backgroundImage);
        } else if (backgroundType === BACKGROUND_TYPE.COLOR) {
            this.setBackgroundColor(backgroundColor);
        } else {
            this._backgroundType = BACKGROUND_TYPE.TRANSPARENT;
            this.updateBackgroundCanvas();
        }
    }

    setBackgroundColor(color) {
        this._backgroundColor = color;
        this._backgroundType = BACKGROUND_TYPE.COLOR;
        this.updateBackgroundCanvas();
    }

    getBackgroundInfo() {
        return {
            backgroundType: this._backgroundType,
            backgroundImage: this._backgroundImageInfo,
            backgroundColor: this._backgroundColor,
        };
    }

    setStrokeInfo(strokeInfo, updateView = true) {
        if (!strokeInfo) {
            this._strokeInfo = null;
        } else {
            this._strokeInfo = { ...strokeInfo };
        }
        updateView && this.updateView();
    }

    getStrokeInfo() {
        return this._strokeInfo;
    }

    setBackgroundType(backgroundType) {
        this._backgroundType = backgroundType;
        this.updateBackgroundCanvas();
    }

    async getRemoveBgImageBlob(maxSize) {
        if (!this._editLoaded) return;

        const size = this._getSize(maxSize);
        const canvas = FJPoolStore.getCanvas(undefined, true);
        canvas.width = size.width;
        canvas.height = size.height;

        const context = canvas.getContext('2d');

        this.updateView();
        context.drawImage(this._canvas, 0, 0, size.width, size.height);

        if (this._backgroundType !== BACKGROUND_TYPE.TRANSPARENT) {
            this.updateBackgroundCanvas();

            context.drawImage(this._canvas, 0, 0, size.width, size.height);
            context.save();
            context.globalCompositeOperation = 'destination-over';
            context.drawImage(this._backgroundCanvas, 0, 0, size.width, size.height);
            context.restore();
        }

        return await new Promise(resolve => {
            canvas
                .convertToBlob()
                .then(blob => {
                    resolve(blob);
                })
                .catch(() => {
                    resolve(null);
                });
        });
    }

    clear() {
        super.clear();

        this._maskCanvas?.clear();
        if (this._backgroundCanvas) {
            this._backgroundCanvasCtx = null;
            FJPoolStore.releaseCanvas(this._backgroundCanvas);
            this._backgroundCanvas = null;
        }

        this._backgroundImage = null;
        this._backgroundImageInfo = null;
        this._backgroundImageLoaded = false;
        this._backgroundImageRect = null;
        this._backgroundType = BACKGROUND_TYPE.TRANSPARENT;
        this._strokeInfo = null;
    }

    setTransparentSize(size) {
        this._transparentSize = size;
    }
}

export default FJPhotoRemoveBgCanvas;
